import {
  ProductCustomizationData,
  ShipmentFeedbackCustomization,
  FullBudgetCriteriaReview,
  LogisticsFeedbackItem,
  MilestoneFeedback,
  ProductFeedbackItem,
  ShipmentFeedbackAttachment,
  ShipmentFeedbackFullBudgetResponse,
  ShipmentFeedbackItem,
  ShipmentFeedbackMarketplaceResponse,
  ShipmentFeedbackMilestonesResponse,
  ShipmentFeedbackPartyInfo,
  ShipmentFeedbackProductDetail,
} from '@/types/logisticsFeedback';

/**
 * Base adapter: Extracts foundational feedback fields with safe defaults.
 * Strictly respects Rule 21: No cross-attribute fallbacks.
 */
export const adaptBaseFeedbackItem = (
  summary: ShipmentFeedbackItem
): LogisticsFeedbackItem => {
  const isSeller = summary.requesterType?.code === 'SELLER';
  const isBidding = summary.requestType?.code === 'BIDDING';

  return {
    id: summary.id,
    orderId: summary.orderSerial || '-',
    logisticsCompanyName: summary.logisticsCompanyName || '-',
    buyerName: isSeller ? '-' : summary.requesterProfileName || '-',
    sellerName: isSeller ? summary.requesterProfileName || '-' : '-',
    rating: summary.requesterFeedback ?? 0,
    createdDate: summary.dateCreated || '-',
    orderType: isBidding ? 'BIDDING' : 'MARKETPLACE',
    hasMilestones: summary.budgetType === 'MILESTONES',
    productsCount: 0,
  };
};

/**
 * Formats a party phone with optional international dialing code.
 */
export const formatPartyPhone = (
  phoneCode?: string | null,
  phoneNumber?: string | null
): string | undefined => {
  if (phoneCode && phoneNumber) {
    return `${phoneCode} ${phoneNumber}`;
  }
  return phoneNumber || undefined;
};

/**
 * Adapts rich seller and buyer party data from feedback detail payloads.
 */
export const adaptParties = (
  seller?: ShipmentFeedbackPartyInfo | null,
  buyer?: ShipmentFeedbackPartyInfo | null,
  base?: { sellerName?: string; buyerName?: string }
) => {
  return {
    sellerName:
      seller?.name ||
      (base?.sellerName !== '-' ? base?.sellerName : undefined) ||
      '-',
    sellerAvatar: seller?.photoUrl || undefined,
    sellerEmail: seller?.email || undefined,
    sellerPhone: formatPartyPhone(seller?.phoneCode, seller?.phoneNumber),
    buyerName:
      buyer?.name ||
      (base?.buyerName !== '-' ? base?.buyerName : undefined) ||
      '-',
    buyerAvatar: buyer?.photoUrl || undefined,
    buyerEmail: buyer?.email || undefined,
    buyerPhone: formatPartyPhone(buyer?.phoneCode, buyer?.phoneNumber),
  };
};

/**
 * Maps the payload customization object into the UI model.
 * Returns undefined when nothing is customized so the dropdown is not shown.
 */
export const adaptCustomization = (
  customization?: ShipmentFeedbackCustomization | null
): ProductCustomizationData | undefined => {
  if (!customization) return undefined;

  const logo = customization.CUSTOM_LOGO;
  const result: ProductCustomizationData = {
    logo: logo
      ? {
          name: logo.name || '-',
          fileUrl: logo.blobFileUrl || undefined,
          imageUrl: logo.blobFileUrl || undefined,
        }
      : undefined,
    privateLabel: customization.PRIVATE_LABEL || undefined,
    color: customization.CUSTOM_COLOR
      ? { name: customization.CUSTOM_COLOR }
      : undefined,
    packaging: customization.CUSTOM_PACKAGING?.name || undefined,
  };

  const hasData = Boolean(
    result.logo || result.privateLabel || result.color || result.packaging
  );
  return hasData ? result : undefined;
};

/**
 * Helper to adapt a single product detail object into ProductFeedbackItem.
 */
export const adaptProductDetail = (
  prod: ShipmentFeedbackProductDetail,
  fallbackId: string,
  review?: ProductFeedbackItem['review']
): ProductFeedbackItem => ({
  id: prod.productId || fallbackId,
  name: prod.productName || '-',
  imageUrl: prod.productImageUrl || '',
  variations: prod.variations || undefined,
  quantity: prod.quantity ?? 0,
  totalPrice: prod.totalPrice ?? 0,
  currency: 'SAR',
  customization: adaptCustomization(prod.customization),
  review,
});

/**
 * Milestones Adapter: Transforms GET /.../milestones response payload into LogisticsFeedbackItem.
 */
export const adaptMilestonesFeedback = (
  payload: ShipmentFeedbackMilestonesResponse,
  fallbackItem?: ShipmentFeedbackItem
): LogisticsFeedbackItem => {
  const summary = payload.summary || fallbackItem;
  if (!summary) throw new Error('Missing feedback summary');

  const base = adaptBaseFeedbackItem(summary);

  const milestones: MilestoneFeedback[] = (payload.milestones || []).map(
    (ms) => {
      const products: ProductFeedbackItem[] = ms.product
        ? [adaptProductDetail(ms.product, `prod-ms-${ms.milestoneNumber}`)]
        : (ms.products || [])
            .filter(Boolean)
            .map((p, idx) =>
              adaptProductDetail(p, `prod-ms-${ms.milestoneNumber}-${idx}`)
            );

      return {
        id: `milestone-${ms.milestoneNumber}`,
        milestoneNumber: ms.milestoneNumber,
        products,
        isReviewed: Boolean(ms.isReviewed),
        shipmentReview: {
          rating: ms.shipmentRating ?? 0,
          notes: ms.shipmentReason || '',
        },
      };
    }
  );

  const productsCount = milestones.reduce(
    (sum, m) => sum + m.products.length,
    0
  );

  const totalReviewed = (payload.milestones || []).filter(
    (m) => m.isReviewed
  ).length;

  const attachments: string[] = (payload.attachments || [])
    .map((a: unknown) =>
      typeof a === 'string'
        ? a
        : (a as ShipmentFeedbackAttachment)?.blobFileUrl || ''
    )
    .filter(Boolean);

  const parties = adaptParties(payload.seller, payload.buyer, base);

  return {
    ...base,
    ...parties,
    hasMilestones: true,
    productsCount,
    milestones,
    overallReview: {
      totalReviewed,
      totalProducts: (payload.milestones || []).length,
      shipmentRating: summary.requesterFeedback ?? 0,
      shipmentNotes: payload.notes || '',
      attachments,
    },
  };
};

/**
 * Marketplace Adapter: Transforms GET /.../marketplace response payload into LogisticsFeedbackItem.
 */
export const adaptMarketplaceFeedback = (
  payload: ShipmentFeedbackMarketplaceResponse,
  fallbackItem?: ShipmentFeedbackItem
): LogisticsFeedbackItem => {
  const summary = payload.summary || fallbackItem;
  if (!summary) throw new Error('Missing feedback summary');

  const base = adaptBaseFeedbackItem(summary);

  const products: ProductFeedbackItem[] = (payload.products || [])
    .map((item, idx) => {
      const prod =
        item.product || (item as unknown as ShipmentFeedbackProductDetail);
      if (!prod || (!prod.productId && !prod.productName)) return null;

      const attachments = (item.attachments || [])
        .map((a) => a.blobFileUrl || '')
        .filter(Boolean);

      const review: ProductFeedbackItem['review'] | undefined =
        item.isReviewed || item.rating != null
          ? {
              rating: item.rating ?? 0,
              reason: item.reason || '',
              date: item.submittedDate || '-',
              comment: item.notes || '',
              attachments,
            }
          : undefined;

      return adaptProductDetail(prod, `prod-marketplace-${idx}`, review);
    })
    .filter((p): p is ProductFeedbackItem => Boolean(p));

  const overallReview = payload.shipment
    ? {
        totalReviewed: 0,
        totalProducts: 0,
        shipmentRating: payload.shipment.rating ?? 0,
        shipmentReason: payload.shipment.reason || '',
        shipmentNotes: payload.shipment.notes || '',
      }
    : undefined;

  const parties = adaptParties(payload.seller, payload.buyer, base);

  return {
    ...base,
    ...parties,
    hasMilestones: false,
    productsCount: products.length,
    products,
    overallReview,
  };
};

/**
 * Full Budget Adapter: Transforms GET /.../full-budget response payload into LogisticsFeedbackItem.
 */
export const adaptFullBudgetFeedback = (
  payload: ShipmentFeedbackFullBudgetResponse,
  fallbackItem?: ShipmentFeedbackItem
): LogisticsFeedbackItem => {
  const summary = payload.summary || fallbackItem;
  if (!summary) throw new Error('Missing feedback summary');

  const base = adaptBaseFeedbackItem(summary);

  const products: ProductFeedbackItem[] = payload.product
    ? [adaptProductDetail(payload.product, 'prod-full-budget')]
    : (payload.products || [])
        .map((item, idx) => {
          const prod =
            item.product || (item as unknown as ShipmentFeedbackProductDetail);
          if (!prod || (!prod.productId && !prod.productName)) return null;
          return adaptProductDetail(prod, `prod-fb-${idx}`);
        })
        .filter((p): p is ProductFeedbackItem => Boolean(p));

  const attachments: string[] = (payload.attachments || [])
    .map((a: unknown) =>
      typeof a === 'string'
        ? a
        : (a as ShipmentFeedbackAttachment)?.blobFileUrl || ''
    )
    .filter(Boolean);

  const fullBudgetReview: FullBudgetCriteriaReview = {
    shipmentRating: payload.shipmentRating ?? undefined,
    shipmentReason: payload.shipmentReason || '',
    notes: payload.notes || '',
    attachments,
    submittedDate: summary.dateCreated || '-',
  };

  const parties = adaptParties(payload.seller, payload.buyer, base);

  return {
    ...base,
    ...parties,
    hasMilestones: false,
    productsCount: products.length,
    products,
    fullBudgetReview,
    overallReview: {
      totalReviewed: 1,
      totalProducts: products.length,
      shipmentRating: payload.shipmentRating ?? summary.requesterFeedback ?? 0,
      shipmentReason: payload.shipmentReason || '',
      shipmentNotes: payload.notes || '',
    },
  };
};
